/**
 * TheSportsDB sports adapter — implements ProviderAdapter<SportsParams, RawSportsDbResponse, SportsData>.
 *
 * CLIENT / SERVER SPLIT (ADR-002 §5):
 *   CLIENT-SIDE:  paramsSchema, dataShape, cacheKey, glanceView
 *   SERVER-SIDE:  buildRequest, responseSchema, normalize
 *
 * `buildRequest` is THE ONLY place a URL exists in the whole system.
 * API keys are INJECTED as a parameter (never hardcoded, never logged).
 *
 * TheSportsDB API docs: https://www.thesportsdb.com/api.php
 * Endpoint: GET https://www.thesportsdb.com/api/v1/json/{apiKey}/eventslast.php?id={teamId}
 *
 * D-010 CONSTRAINTS:
 *   - Plan: $9/mo — 2-minute delayed livescores tier
 *   - Attribution required: "Data from TheSportsDB.com" — must appear in tile chrome
 *   - Attribution is NOT in DATA_SHAPE_KEYS['sports.score'] — this is flagged for
 *     review. Attribution belongs in tile chrome (rendered by platform-dev), not
 *     in the data shape. See final report.
 *   - Link-out for live data: if status is 'live', the tile should link to the
 *     TheSportsDB event page. The URL is in the raw data but not in the data shape
 *     (DATA_SHAPE_KEYS doesn't include it). This is a tile chrome responsibility.
 *
 * ENDPOINT DESIGN:
 *   Uses `eventslast.php?id={teamId}` — the "last 5 events for a team" endpoint.
 *   This is the best v1 endpoint for "show me my team's latest score":
 *   - Always returns completed results (no unplayed fixture confusion)
 *   - Works for all sports uniformly
 *   - The most recent event is at `events[events.length - 1]`
 *
 * Identity:
 *   type     = 'sports.score'
 *   provider = 'thesportsdb'
 *   tier     = 'free'
 *   ttlSec   = 60 (60s per in-progress game per registry ADR-001 §3)
 */

import { z } from 'zod';
import { sportsParamsSchema, deriveCacheKey, type SportsParams } from 'briefpane/schema';
import type { GlanceMap } from 'briefpane/schema';
import type { GlanceViewModel, ProviderAdapter, UpstreamRequest } from 'briefpane/contracts';
import {
  rawSportsDbResponseSchema,
  normalize,
  type RawSportsDbResponse,
  type SportsData,
} from './normalize.js';
import { glanceView } from './glance.js';

// ---------------------------------------------------------------------------
// dataShape Zod schema
// Keys must match DATA_SHAPE_KEYS['sports.score']:
//   score, clock, status, opponent, result, nextFixtureAt
// ---------------------------------------------------------------------------

const sportsDataShape = z.object({
  score: z.string(),
  clock: z.string(),
  status: z.string(),
  opponent: z.string(),
  result: z.string(),
  nextFixtureAt: z.string().nullable(),
});

// ---------------------------------------------------------------------------
// The adapter object
// ---------------------------------------------------------------------------

/**
 * The sports adapter with a build-request signature that accepts an injected API key.
 */
export interface SportsAdapterShape
  extends Omit<ProviderAdapter<SportsParams, RawSportsDbResponse, SportsData>, 'buildRequest' | 'normalize'> {
  /** Server-side: build the TheSportsDB upstream request, injecting the API key. */
  buildRequest(params: SportsParams, apiKey: string): UpstreamRequest;
  /** Map normalized data + glance map to the H3 view-model. */
  glanceView(data: SportsData, glance: GlanceMap): GlanceViewModel;
  /** Normalize the TheSportsDB response; teamId context required for W/L determination. */
  normalize(raw: RawSportsDbResponse, context: { teamId: string }): SportsData;
}

/**
 * The TheSportsDB sports score adapter.
 *
 * D-010: 2-minute delayed scores, attribution required in tile chrome,
 * link-out for live data. See module doc above.
 */
export const sportsAdapter: SportsAdapterShape = {
  // ---- Identity (both halves) ----
  type: 'sports.score',
  provider: 'thesportsdb',
  tier: 'free',
  ttlSec: 60,

  // ---- CLIENT-SIDE ----
  paramsSchema: sportsParamsSchema,
  dataShape: sportsDataShape,

  cacheKey: (params: SportsParams): string =>
    deriveCacheKey({ type: 'sports.score', provider: 'thesportsdb', params }),

  glanceView: (data: SportsData, glance: GlanceMap): GlanceViewModel =>
    glanceView(data, glance),

  // ---- SERVER-SIDE ONLY ----
  responseSchema: rawSportsDbResponseSchema,

  /**
   * Build the TheSportsDB eventslast request.
   *
   * THE ONLY PLACE A URL EXISTS IN THE SYSTEM (ADR-002 §5, §7 #1).
   * Never call this client-side. The `apiKey` is injected from server env.
   * Never log the returned URL — it contains the key.
   *
   * We use eventslast.php (last 5 events for a team) rather than livescores
   * because: (a) it always has data for any past games, (b) livescores requires
   * a higher API tier, (c) the "last score" is the primary v1 use case.
   *
   * The `teamId` from params is the TheSportsDB team numeric ID as a string.
   * The `league` param from sportsParamsSchema is not used in the URL — it's
   * in the params for cache-key purposes and future per-league endpoints.
   *
   * TheSportsDB API: https://www.thesportsdb.com/api.php
   *   eventslast.php?id={teamId} — last 5 events for a team
   */
  buildRequest: (params: SportsParams, apiKey: string): UpstreamRequest => ({
    url: `https://www.thesportsdb.com/api/v1/json/${apiKey}/eventslast.php?id=${params.teamId}`,
    method: 'GET',
  }),

  normalize: (raw: RawSportsDbResponse, context: { teamId: string }): SportsData =>
    normalize(raw, context),
};
